import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { SelectComponent, TextComponent, TooltipComponent } from '../../uiComponents';
import { Checkbox, FormControlLabel, SelectChangeEvent, SelectClassKey } from '@mui/material';
import { OptionProps, SelectPropsData, TextPropsData } from '../../../view-models';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import constants from '../../../constants/constants';
import jsonConstants from '../../../constants/jsonConstants';
import { ResolverSelector } from './resolverSelector';
import { getTemplatizationFields } from '../../../store';
import { useSelector } from 'react-redux';
import { ClaimFunctionSelectorComponent } from './claimFunctionSelectors/claimFunctionSelectorComponent';

const styles1 = makeStyles((theme: Theme) =>
  createStyles({
    flexCol: {
      display: 'flex',
      flexDirection: 'column',
    },
    flexColfill: {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '100%',
      flex: '1',
      padding: '2px',
      overflow: 'hidden',
      '& .inline-tooltip': {
        marginTop: '10px',
      },
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
    },
    attribute: {
      borderBottom: '1px solid grey',
      padding: 10,
    },
    pointer: {
      cursor: 'pointer',
    },
    icon: {
      cursor: 'pointer',
      marginLeft: '0.5rem',
    },
    justifyContent: {
      justifyContent: 'space-between',
    },
    marginRight: { marginRight: '20px' },
    marginLeft: { marginLeft: '20px' },
    displayAttribute: {
      flex: 1,
    },
    actions: {
      width: 90,
    },
  }),
);

interface CheckboxPropsData {
  label: string;
}

interface ExtraFieldInfo {
  fieldName: string;
  fieldType: 'select' | 'text' | 'checkbox';
  fieldData: SelectPropsData | TextPropsData | CheckboxPropsData;
}

type FieldMapperSource = 'claim' | 'literal' | 'resolver' | 'appsetting' | 'transform';

interface FieldMapperRowProps {
  rowData: any;
  writeback: (rowData: any) => void;
  claimsList: OptionProps[];
  allClaims?: any;
  enabledSources?: FieldMapperSource[];
  precursorFields?: ExtraFieldInfo[];
  postFields?: ExtraFieldInfo[];
  sourceTypeFieldName?: string;
  sourceTypeFieldLabel?: string;
  fixedSourceType?: string;
  literalDataType?: string;
  useBaseResolversOnly?: boolean;
  sourceFieldName: string;
  sourceFieldLabels?: { [sourceType: string]: string };
  partnerTypeFieldName?: string;
  partnerTypeFieldLabel?: string;
  defaultValueFieldName?: string;
  defaultValueFieldLabel?: string;
  allowEmptyDefaultValue?: boolean;
  alwaysUseDefaultValueFieldName?: string;
  defaultValueErrorText?: string;
  buttonText?: string;
  buttonAction?: any;
  buttonDisabled?: boolean;
  templateFieldList?: OptionProps[];
  transformOptions?: OptionProps[];
  transformFieldName?: string;
}

const baseOptions: OptionProps[] = [
  { value: 'claim', label: 'Claim' },
  { value: 'literal', label: 'Literal' },
  { value: 'resolver', label: 'Session Context' },
  { value: 'appsetting', label: 'App Setting' },
  { value: 'transform', label: 'Function' }
];

export const FieldMapperRow = ({
  rowData,
  writeback,
  claimsList,
  allClaims,
  enabledSources,
  precursorFields,
  postFields,
  sourceTypeFieldName,
  sourceTypeFieldLabel,
  fixedSourceType,
  literalDataType,
  useBaseResolversOnly=false,
  sourceFieldName,
  sourceFieldLabels,
  partnerTypeFieldName,
  partnerTypeFieldLabel,
  defaultValueFieldName,
  defaultValueFieldLabel,
  allowEmptyDefaultValue,
  alwaysUseDefaultValueFieldName,
  defaultValueErrorText,
  buttonText,
  buttonAction,
  buttonDisabled,
  templateFieldList,
  transformOptions,
  transformFieldName
}: FieldMapperRowProps) => {
  const classes = styles1();

  return (
    <div className={classes.flexRow}>
      {precursorFields &&
        precursorFields.map((precursorField, idx) => {
          if (precursorField.fieldType === 'select') {
            return (
              <SelectComponent
                key={idx}
                data={precursorField.fieldData as SelectPropsData}
                changeHandler={(e: SelectChangeEvent<string>) => {
                  writeback({
                    ...rowData,
                    [precursorField.fieldName]: e.target.value,
                  });
                }}
              />
            );
          } else if (precursorField.fieldType === 'text') {
            return (
              <TextComponent
                key={idx}
                data={precursorField.fieldData as TextPropsData}
                changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  writeback({
                    ...rowData,
                    [precursorField.fieldName]: e.target.value,
                  });
                }}
              />
            );
          } else if (precursorField.fieldType === 'checkbox') {
            return (
              <FormControlLabel
                sx={{ m: 0, width: '300px' }}
                label={precursorField.fieldData.label}
                control={
                  <Checkbox
                    checked={rowData[precursorField.fieldName]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      writeback({
                        ...rowData,
                        [precursorField.fieldName]: !rowData[precursorField.fieldName],
                      });
                    }}
                  />
                }
              />
            );
          }
        })}
      {sourceTypeFieldName && (
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            writeback({
              ...rowData,
              [sourceTypeFieldName]: e.target.value,
              [sourceFieldName]: e.target.value === 'resolver' ? constants.SESSIONCONTEXT[0].value :
                (e.target.value === 'appsetting' ? templateFieldList![0].value :
                  (e.target.value === 'transform' ? transformOptions![0].value : '')),
              ...(transformFieldName && { [transformFieldName]: (e.target.value === 'transform' ? (rowData[transformFieldName] ? rowData[transformFieldName] : {}) : {})})
            });
          }}
          data={{
            value: rowData[sourceTypeFieldName] ? rowData[sourceTypeFieldName] : '',
            label: sourceTypeFieldLabel ? sourceTypeFieldLabel : '',
            options: enabledSources
              ? baseOptions.filter((opt) => {
                  return enabledSources.includes(opt.value as FieldMapperSource);
                })
              : [
                  { value: 'claim', label: 'Claim' },
                  { value: 'literal', label: 'Literal' },
                  { value: 'resolver', label: 'Session Context' },
                ],
            style: { margin: 1, width: 200 },
          }}
        />
      )}
      {(fixedSourceType === 'claim' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'claim')) && (
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            const wbObj = {
              ...rowData,
              [sourceFieldName]: e.target.value,
            };
            if (
              defaultValueFieldName &&
              (!rowData[sourceFieldName] ||
                !e.target.value ||
                allClaims[e.target.value].dataType !== allClaims[rowData[sourceFieldName]].dataType)
            ) {
              if (allClaims[e.target.value].dataType === 'boolean') {
                wbObj[defaultValueFieldName] = 'false';
              } else if (allClaims[e.target.value].dataType === 'string') {
                wbObj[defaultValueFieldName] = '';
              }
            }
            writeback(wbObj);
          }}
          data={{
            value: rowData[sourceFieldName] ? rowData[sourceFieldName] : '',
            label: sourceFieldLabels?.claim ? sourceFieldLabels.claim : '',
            options: claimsList,
            style: { margin: 1, width: 250 },
          }}
        />
      )}
      {(fixedSourceType === 'resolver' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'resolver')) && (
        <ResolverSelector
          changeHandler={(rValue: string) => {
            writeback({
              ...rowData,
              [sourceFieldName]: rValue,
            });
          }}
          value={rowData[sourceFieldName]}
          label={sourceFieldLabels?.resolver ? sourceFieldLabels.resolver : ''}
          baseResolversOnly={useBaseResolversOnly}
        />
      )}
      {(fixedSourceType === 'literal' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'literal')) && (
        <>
          {((!literalDataType) || (literalDataType === 'string')) && (
            <TextComponent
              changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                writeback({
                  ...rowData,
                  [sourceFieldName]: e.target.value,
                });
              }}
              data={{
                value: rowData[sourceFieldName] ? rowData[sourceFieldName] : '',
                label: sourceFieldLabels?.literal ? sourceFieldLabels.literal : '',
                style: { margin: 1, width: 350 },
              }}
            />
          )}
          {(literalDataType === 'boolean') && (
            <SelectComponent
              changeHandler={(e: SelectChangeEvent) => {
                writeback({
                  ...rowData,
                  [sourceFieldName]: e.target.value,
                });
              }}
              data={{
                value: rowData[sourceFieldName] ? rowData[sourceFieldName] : 'true',
                label: sourceFieldLabels?.literal ? sourceFieldLabels.literal : '',
                style: { margin: 1, width: 350 },
                options: [
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false'}
                ]
              }}
            />
          )}
        </>
      )}
      {(fixedSourceType === 'appsetting' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'appsetting')) && (
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            writeback({
              ...rowData,
              [sourceFieldName]: e.target.value
            });
          }}
          data={{
            label: sourceFieldLabels?.appsetting ? sourceFieldLabels.appsetting : '',
            value: rowData[sourceFieldName] ? rowData[sourceFieldName] : '',
            style: { margin: 1, width: 350 },
            options: templateFieldList!
          }}
        />
      )}
      {(fixedSourceType === 'transform' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'transform')) && transformFieldName && (
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            writeback({
              ...rowData,
              [sourceFieldName]: e.target.value
            })
          }}
          data={{
            label: sourceFieldLabels?.transform ? sourceFieldLabels.transform : '',
            value: rowData[sourceFieldName] ? rowData[sourceFieldName] : '',
            style: { margin: 1, width: 350 },
            options: transformOptions ? transformOptions : []
          }}
        />
      )}
      {partnerTypeFieldName && (
        <TextComponent
          changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            writeback({
              ...rowData,
              [partnerTypeFieldName]: e.target.value,
            });
          }}
          data={{
            value: rowData[partnerTypeFieldName] ? rowData[partnerTypeFieldName] : '',
            label: partnerTypeFieldLabel ? partnerTypeFieldLabel : '',
            style: { margin: 1, width: 350 },
          }}
        />
      )}
      {defaultValueFieldName &&
        (fixedSourceType === 'claim' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'claim')) &&
        sourceFieldName &&
        rowData[sourceFieldName] &&
        ['boolean', 'string'].includes(allClaims[rowData[sourceFieldName]].dataType) && (
          <>
            {allClaims[rowData[sourceFieldName]]?.dataType === 'boolean' && (
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  writeback({
                    ...rowData,
                    [defaultValueFieldName]: e.target.value,
                  });
                }}
                data={{
                  value: rowData[defaultValueFieldName] ? rowData[defaultValueFieldName] : 'false',
                  label: defaultValueFieldLabel ? defaultValueFieldLabel : '',
                  options: [
                    { value: 'true', label: 'true' },
                    { value: 'false', label: 'false' },
                  ],
                  style: { margin: 1, width: 250 },
                }}
              />
            )}
            {allClaims[rowData[sourceFieldName]]?.dataType === 'string' && (
              <TextComponent
                changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  writeback({
                    ...rowData,
                    [defaultValueFieldName]: e.target.value,
                  });
                }}
                data={{
                  value: rowData[defaultValueFieldName] ? rowData[defaultValueFieldName] : '',
                  label: defaultValueFieldLabel ? defaultValueFieldLabel : '',
                  style: { margin: 1, width: 350 },
                  error: !allowEmptyDefaultValue && !rowData[defaultValueFieldName],
                  helperText:
                    defaultValueErrorText && !allowEmptyDefaultValue && !rowData[defaultValueFieldName]
                      ? defaultValueErrorText
                      : '',
                }}
              />
            )}
            {alwaysUseDefaultValueFieldName && (
              <FormControlLabel
                sx={{ m: 0, width: '300px' }}
                label="Always Use Default Value"
                control={
                  <Checkbox
                    checked={rowData[alwaysUseDefaultValueFieldName]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      writeback({
                        ...rowData,
                        [alwaysUseDefaultValueFieldName]: !rowData[alwaysUseDefaultValueFieldName],
                      });
                    }}
                  />
                }
              />
            )}
          </>
        )}
      {(fixedSourceType === 'transform' || (sourceTypeFieldName && rowData[sourceTypeFieldName] === 'transform')) && transformFieldName && (
        <ClaimFunctionSelectorComponent
          data={rowData}
          transformFieldName={transformFieldName}
          claimFunction={rowData[sourceFieldName]}
          claimsList={claimsList}
          allClaims={allClaims}
          writeback={writeback}
        />
      )}
      {postFields &&
        postFields.map((postField, idx) => {
          if (postField.fieldType === 'select') {
            return (
              <SelectComponent
                key={idx}
                data={postField.fieldData as SelectPropsData}
                changeHandler={(e: SelectChangeEvent<string>) => {
                  writeback({
                    ...rowData,
                    [postField.fieldName]: e.target.value,
                  });
                }}
              />
            );
          } else if (postField.fieldType === 'text') {
            return (
              <TextComponent
                key={idx}
                data={postField.fieldData as TextPropsData}
                changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  writeback({
                    ...rowData,
                    [postField.fieldName]: e.target.value,
                  });
                }}
              />
            );
          } else if (postField.fieldType === 'checkbox') {
            return (
              <FormControlLabel
                sx={{ m: 0, width: '300px' }}
                label={postField.fieldData.label}
                control={
                  <Checkbox
                    checked={rowData[postField.fieldName]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      writeback({
                        ...rowData,
                        [postField.fieldName]: !rowData[postField.fieldName],
                      });
                    }}
                  />
                }
              />
            );
          }
        })}
      {buttonAction && (
        <Button
          variant="contained"
          sx={{ alignSelf: 'center' }}
          disabled={buttonDisabled ? buttonDisabled : false}
          onClick={buttonAction}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};
